import { Controller } from '@hotwired/stimulus'
import type { AppI18nProvider } from '../../components/AppI18nProvider'
import * as meta from './meta'

// Types for the violation guide
interface ViolationRule {
  ruleNumber: string
  ruleSection: string
  violationText: string
  discipline: string[]
  category?: string
  phase?: string
  detail?: string
}

interface SessionState {
  selectedDiscipline: string | null
  currentQuestionIndex: number
  answeredQuestions: Map<string, string>
  breadcrumbPath: string[]
  currentViolation: ViolationRule | null
  language: string
  sessionStartTime: number
}

export default class extends Controller {
  static override targets = [
    'welcomeScreen',
    'disciplineSelect',
    'questionScreen',
    'resultScreen',
    'navBar',
    'breadcrumb',
    'progressBar',
    'backButton',
    'resetButton',
    'questionText',
    'optionsContainer',
    'violationResult',
    'languageToggle',
    'i18nProvider',
  ]

  static override values = {
    translations: Object,
  }

  declare readonly welcomeScreenTarget: HTMLElement
  declare readonly disciplineSelectTarget: HTMLSelectElement
  declare readonly questionScreenTarget: HTMLElement
  declare readonly resultScreenTarget: HTMLElement
  declare readonly navBarTarget: HTMLElement
  declare readonly breadcrumbTarget: HTMLElement
  declare readonly progressBarTarget: HTMLElement
  declare readonly backButtonTarget: HTMLButtonElement
  declare readonly resetButtonTarget: HTMLButtonElement
  declare readonly questionTextTarget: HTMLElement
  declare readonly optionsContainerTarget: HTMLElement
  declare readonly violationResultTarget: HTMLElement
  declare readonly languageToggleTarget: HTMLElement
  declare readonly i18nProviderTarget: AppI18nProvider

  declare translationsValue: Record<string, any>

  #state!: SessionState
  #decisionTree!: Map<string, any>
  #rules!: ViolationRule[]

  /**
   * Escapes HTML special characters to prevent XSS attacks
   * Converts <, >, &, ", and ' to their HTML entity equivalents
   */
  #escapeHtml(unsafe: string): string {
    const div = document.createElement('div')
    div.textContent = unsafe
    return div.innerHTML
  }

  override connect(): void {
    // Initialize decision tree and rules first
    this.#initializeDecisionTree()
    this.#initializeRules()

    // Initialize state
    this.#state = this.#loadSessionState() || this.#createInitialState()

    // Restore session if exists
    if (this.#state.selectedDiscipline) this.#restoreSession()
    else this.#showWelcomeScreen()
  }

  #createInitialState(): SessionState {
    return {
      selectedDiscipline: null,
      currentQuestionIndex: 0,
      answeredQuestions: new Map(),
      breadcrumbPath: [],
      currentViolation: null,
      language: document.documentElement.lang || 'de',
      sessionStartTime: Date.now(),
    }
  }

  #loadSessionState(): SessionState | null {
    const stored = sessionStorage.getItem('swimming_guide_state')
    if (!stored) return null

    try {
      const parsed = JSON.parse(stored)
      // Check if session is less than 24 hours old
      if (Date.now() - parsed.sessionStartTime > 24 * 60 * 60 * 1000) return null

      // Convert answeredQuestions back to Map
      parsed.answeredQuestions = new Map(Object.entries(parsed.answeredQuestions || {}))
      return parsed
    } catch {
      return null
    }
  }

  #saveSessionState(): void {
    const toSave = {
      ...this.#state,
      answeredQuestions: Object.fromEntries(this.#state.answeredQuestions),
    }
    sessionStorage.setItem('swimming_guide_state', JSON.stringify(toSave))
  }

  #initializeRules(): void {
    const violations = this.translationsValue.violations || {}
    this.#rules = []

    // Parse nested YAML structure
    Object.entries(violations).forEach(([paraKey, paraData]: [string, any]) => {
      const ruleNumber = paraData.rule_number
      const ruleSection = paraData.rule_section

      // Iterate through subsections (e.g., delayed_position, provoked_false_start)
      Object.entries(paraData).forEach(([detailKey, detailData]: [string, any]) => {
        // Skip metadata keys
        if (detailKey === 'rule_number' || detailKey === 'rule_section') return

        const fullKey = `${paraKey}.${detailKey}`
        const metadata = meta.VIOLATIONS[fullKey] || { discipline: ['general'] }

        this.#rules.push({
          ruleNumber,
          ruleSection,
          violationText: detailData.text,
          discipline: metadata.discipline,
          category: metadata.category,
          detail: detailKey,
        })
      })
    })
  }

  #initializeDecisionTree(): void {
    // Decision tree structure: discipline -> question sequence
    this.#decisionTree = meta.DECISION_TREE
  }

  // UC1: Initial Page Load & Language Setup
  #showWelcomeScreen(): void {
    this.welcomeScreenTarget.classList.remove('hidden')
    this.questionScreenTarget.classList.add('hidden')
    this.resultScreenTarget.classList.add('hidden')
    this.navBarTarget.classList.add('hidden')
    this.resetButtonTarget.disabled = true
  }

  // UC2: Discipline Selection
  selectDiscipline(event: Event): void {
    const select = event.target as HTMLSelectElement
    const discipline = select.value

    if (!discipline) return

    this.#state.selectedDiscipline = discipline
    this.#state.currentQuestionIndex = 0
    this.#state.answeredQuestions.clear()
    this.#state.breadcrumbPath = [discipline]
    this.resetButtonTarget.disabled = false

    this.#saveSessionState()
    this.#showFirstQuestion()
  }

  // UC3: Dynamic Branching Questionnaire
  #showFirstQuestion(): void {
    this.welcomeScreenTarget.classList.add('hidden')
    this.questionScreenTarget.classList.remove('hidden')
    this.resultScreenTarget.classList.add('hidden')
    this.navBarTarget.classList.remove('hidden')

    const tree = this.#decisionTree.get(this.#state.selectedDiscipline!)
    if (!tree) {
      this.#showError('invalid_discipline')
      return
    }

    this.#renderQuestion('q1', tree.q1)
    this.#updateBreadcrumb()
    this.#updateProgress()
  }

  #renderQuestion(questionId: string, question: any): void {
    if (!question) {
      this.#showError('unknown_question')
      return
    }

    // Get translated question text using i18n component
    const questionKey = `questions.${question.text}`
    this.questionTextTarget.innerHTML = `<app-i18n-t key="${questionKey}"></app-i18n-t>`

    // Render options
    this.optionsContainerTarget.innerHTML = ''
    question.options.forEach((option: string) => {
      const button = document.createElement('button')
      button.className = 'btn btn-lg btn-accent btn-block justify-start'
      button.dataset.action = 'click->swimming-guide#selectAnswer'
      button.dataset.questionId = questionId
      button.dataset.answer = option
      button.innerHTML = `<app-i18n-t key="answers.${option}"></app-i18n-t>`
      this.optionsContainerTarget.appendChild(button)
    })
  }

  selectAnswer(event: Event): void {
    const button = event.currentTarget as HTMLButtonElement
    const questionId = button.dataset.questionId!
    const answer = button.dataset.answer!

    // Save answer
    this.#state.answeredQuestions.set(questionId, answer)
    this.#state.breadcrumbPath.push(answer)
    this.#state.currentQuestionIndex++

    this.#saveSessionState()

    // Determine next question or show result
    const tree = this.#decisionTree.get(this.#state.selectedDiscipline!)
    const nextQuestion = tree?.[answer]

    if (nextQuestion && nextQuestion.text) {
      this.#renderQuestion(answer, nextQuestion)
      this.#updateBreadcrumb()
      this.#updateProgress()
    } else {
      // Find matching violation
      this.#findAndShowViolation(answer)
    }
  }

  // UC4: Decision Narrowing & Violation Identification
  #findAndShowViolation(finalAnswer: string): void {
    const discipline = this.#state.selectedDiscipline!

    // Find matching rule
    const violation = this.#rules.find(
      (r) => (r.discipline.includes(discipline) || r.discipline.includes('all')) && r.detail === finalAnswer,
    )

    if (!violation) {
      this.#showError('no_matching_rule')
      return
    }

    this.#state.currentViolation = violation
    this.#saveSessionState()
    this.#showResult()
  }

  #showResult(): void {
    this.welcomeScreenTarget.classList.add('hidden')
    this.questionScreenTarget.classList.add('hidden')
    this.resultScreenTarget.classList.remove('hidden')
    this.navBarTarget.classList.remove('hidden')

    const violation = this.#state.currentViolation!
    // Sanitize all violation data to prevent XSS
    const safeRuleNumber = this.#escapeHtml(violation.ruleNumber)
    const safeRuleSection = this.#escapeHtml(violation.ruleSection)
    const safeViolationText = this.#escapeHtml(violation.violationText)
    const title = `${safeRuleNumber} - ${safeRuleSection}`

    this.violationResultTarget.innerHTML = `
      <div class="space-y-4">
        <article class="card card--guide">
          <div class="card-body">
            <span class="badge badge-accent">
              <app-i18n-t key="violation_title"></app-i18n-t>
            </span>
            <h3 class="card-title">${title}</h3>
            <p>${safeViolationText}</p>
          </div>
        </article>
      </div>
    `

    // Keep back button visible on result screen so users can go back and change answers
    this.backButtonTarget.classList.remove('invisible')

    // Enable reset button on result screen so users can start over
    this.resetButtonTarget.disabled = false
  }

  // UC6: Session Reset
  reset(): void {
    sessionStorage.removeItem('swimming_guide_state')
    this.#state = this.#createInitialState()
    this.disciplineSelectTarget.value = ''
    this.#showWelcomeScreen()
  }

  // UC7: Session Persistence
  #restoreSession(): void {
    // If there's a current violation, show result
    if (this.#state.currentViolation) {
      this.#showResult()
    } else {
      // Continue from where they left off
      this.#showFirstQuestion()
    }
  }

  // UC9: Error Handling
  #showError(errorType: string): void {
    const errorMessage = this.i18nProviderTarget.getTranslation(`errors.${errorType}`)
    alert(errorMessage)
    const errorTypes = ['invalid_discipline', 'corrupted_session', 'no_matching_rule']

    if (errorTypes.includes(errorType)) this.reset()
  }

  // UC10: Navigation & UX Features
  #updateBreadcrumb(): void {
    this.breadcrumbTarget.innerHTML = this.#state.breadcrumbPath
      .map((item, index) => {
        const isLast = index === this.#state.breadcrumbPath.length - 1
        const breadcrumbClass = isLast ? 'font-bold' : 'link link-hover'
        // First item is always discipline, use breadcrumb.* key
        // Subsequent items are answers, use answers.* key
        const i18nKey = index === 0 ? `breadcrumb.${item}` : `answers.${item}`
        return `<li class="${breadcrumbClass}"><app-i18n-t key="${i18nKey}"></app-i18n-t></li>`
      })
      .join('')
    this.backButtonTarget.classList[this.#state.breadcrumbPath.length === 1 ? 'add' : 'remove']('invisible')
  }

  #updateProgress(): void {
    const totalQuestions = this.#estimateTotalQuestions()
    const current = this.#state.currentQuestionIndex + 1
    const percentage = (current / totalQuestions) * 100

    // Sanitize JSON string to prevent XSS in attribute context
    const safeVarsJson = this.#escapeHtml(JSON.stringify({ current, total: totalQuestions }))

    this.progressBarTarget.innerHTML = `
      <progress class="progress w-full" value="${percentage}" max="100"></progress>
      <p class="text-sm text-center mt-2">
        <app-i18n-t key="question_progress" vars='${safeVarsJson}'></app-i18n-t>
      </p>
    `
  }

  #estimateTotalQuestions(): number {
    // Most paths are 2-3 questions
    return 3
  }

  goBack(): void {
    if (this.#state.breadcrumbPath.length <= 1) return

    // Clear violation state if we're going back from result screen
    this.#state.currentViolation = null

    // Ensure result screen is hidden and question screen is shown
    this.resultScreenTarget.classList.add('hidden')
    this.questionScreenTarget.classList.remove('hidden')
    this.welcomeScreenTarget.classList.add('hidden')
    this.navBarTarget.classList.remove('hidden')

    // Remove last answer from breadcrumb
    this.#state.breadcrumbPath.pop()

    // Remove last answered question from the map
    const questionKeys = Array.from(this.#state.answeredQuestions.keys())
    const lastQuestionId = questionKeys[questionKeys.length - 1]
    if (lastQuestionId) {
      this.#state.answeredQuestions.delete(lastQuestionId)
    }

    this.#state.currentQuestionIndex--

    this.#saveSessionState()

    // Re-render previous question
    if (this.#state.currentQuestionIndex === 0) {
      // Going back to first question
      this.#showFirstQuestion()
    } else {
      // Navigate back through the tree by replaying answers
      const selectedDiscipline = this.#state.selectedDiscipline
      if (!selectedDiscipline) return

      const tree = this.#decisionTree.get(selectedDiscipline)
      if (!tree) return

      // Start from q1 and replay all answers except the last one
      let currentNode: any = tree.q1
      let questionId = 'q1'

      // Replay answers to get to the previous question
      const answersArray = Array.from(this.#state.answeredQuestions.values())
      for (const answer of answersArray) {
        const nextNode = tree[answer as keyof typeof tree]
        if (nextNode && typeof nextNode === 'object') {
          currentNode = nextNode
          questionId = answer
        }
      }

      // Render the question we should be at
      if (currentNode) this.#renderQuestion(questionId, currentNode)
    }

    this.#updateBreadcrumb()
    this.#updateProgress()
  }
}
